import React, { useState, useEffect, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished, BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import { getTeamLogoUrl as getTeamLogoUrlDirect } from '../utils/formatters';
import GameHeader from './GameHeader';
import H2hAllTable from './H2hAllTable';
import H2hMiniTable from './H2hMiniTable';
import TotalsTable from './TotalsTable';
import TotalsMiniTable from './TotalsMiniTable';
import SpreadsTable from './SpreadsTable';
import SpreadsMiniTable from './SpreadsMiniTable';
import UpgradeButton from './UpgradeButton';

const NFLDataTable = ({
  data,
  gameTime,
  gameStatus,
  gameDate,
  lastUpdate,
  game,
  formatPlayerName,
  getTeamLogoUrl,
  getBookLogoUrl,
  isMobile,
  selectedMarket,
  selectedMarketName,
  bookmakers,
  isLoading,
  availableMarkets,
  onMarketSelection,
  showTiers,
  onTierToggle,
  masterTierFilter,
  onPlayerClick,
  onPlayerContextMenu,
  onOddsClick,
  clickedCellId,
  isCollapsed = false,
  onCollapseToggle,
  onSettingsClick,
  nflViewMode = 'ou' // 'ou' for O/U view, 'bestline' for Edge view
}) => {

  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameId: 'loading', gameIDmlb: 'loading', sport: 'nfl', ...game };

  const handleNameContextMenu = (player) => (e) => {
    if (!onPlayerContextMenu) return;
    const teamAbbr =
      player?.teamAbv ||
      (player?.team === 'away' ? safeGame.away : player?.team === 'home' ? safeGame.home : player?.team) ||
      player?.teamId ||
      null;
    onPlayerContextMenu(e, safeGame.gameIDmlb, player?.playerId, player?.name, teamAbbr);
  };

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Track rendering state - prevent heavy processing until spinner paints
  const [isRendering, setIsRendering] = useState(false);
  const prevMarketRef = useRef(selectedMarket);
  // Books enabled for Best Bets — used to gate which books participate in
  // the Middles calculation. Falls back to all bookmakers if the setting
  // hasn't been populated yet.
  const { isBookEnabledForBestBets } = useBooks();

  // Detect market change DURING render (not in useEffect)
  if (prevMarketRef.current !== selectedMarket) {
    if (!isRendering) {
      // Market just changed, set rendering state synchronously
      setIsRendering(true);
    }
    prevMarketRef.current = selectedMarket;
  }

  // Clear rendering state after spinner has painted
  useEffect(() => {
    if (isRendering) {
      const timeoutId = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isRendering]);

  // Combined loading state
  const showLoading = isLoading || isRendering;

  // Block rendering heavy data while loading
  const displayData = showLoading ? [] : data;

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;

  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);
  
  // Get visible books based on platform
  const bookLogos = isMobile 
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;

  const oddsCellStyle = { 
    width: '60px',
    minWidth: '60px',
    maxWidth: '60px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Tier color styling function (copied from original DataTable)
  const getCellStyle = (price, bestBookPrice, bestBookDiff, count, direction, allPlayerLines = null, currentBook = null, market = null) => {
    const style = {};

    // For Edge View, count how many books have this exact odds across all lines
    let occurrences = 1;
    if (allPlayerLines && currentBook && market) {
      occurrences = 0;
      const bookAlias = BOOKMAKER_ALIASES[currentBook] || currentBook;

      // Check all visible books to see if they also have this odds value
      bookLogos.forEach(book => {
        const checkAlias = BOOKMAKER_ALIASES[book] || book;
        allPlayerLines.forEach(line => {
          const bookOdds = line[`${market}_${checkAlias}_over`] || line[`${market}_${checkAlias}`] ||
                          line[`${market}_${book}_over`] || line[`${market}_${book}`];
          if (bookOdds === price) {
            occurrences++;
          }
        });
      });
    }

    // Always bold best odds when there are multiple books and it's the unique best price
    if (price === bestBookPrice && count > 1 && occurrences === 1) {
      style.fontWeight = 'bold';

      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text (original design)
          Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text for better contrast
          Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
          Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
        } else {
          // Best line but below tier thresholds - use tier 2 amber color
          Object.assign(style, { color: '#F59E0B' });
        }
      }
    }

    return style;
  };

  const getTooltipText = (price, bestBookPrice, bestBookDiff, count) => {
    const occurrences = [price].filter(p => p === bestBookPrice).length;
    if (price === bestBookPrice && bestBookDiff !== 1 && count > 1 && occurrences === 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue > 0) {
        return `${(diffValue * 100).toFixed(1)}% edge`;
      }
    }
    return null;
  };

  // Calculate best line for a player/book combo based on edge (for Edge View)
  // Takes all lines for a player and finds the OVER with best edge for the specified book
  const getBestLineByEdge = (allPlayerLines, book, market) => {
    let bestEdgeOverLine = null;
    let bestEdgeDiff = -Infinity;
    let highestPointMainLine = null;
    let highestPoint = -Infinity;

    // Handle bookmaker aliases (e.g., williamhill_us -> caesars)
    const bookAlias = BOOKMAKER_ALIASES[book] || book;

    // Iterate through all lines (different point values) for this player
    allPlayerLines.forEach(line => {
      // Try both the original book name and its alias
      const overOdds = line[`${market}_${bookAlias}_over`] || line[`${market}_${bookAlias}`] ||
                       line[`${market}_${book}_over`] || line[`${market}_${book}`];
      const underOdds = line[`${market}_${bookAlias}_under`] || line[`${market}_${book}_under`];
      const bookPoint = line.point;
      const bestBookPrice = line[`${market}_bestBookPrice`];
      const bestBookDiff = line[`${market}_bestBookDiff`];
      const isAlternate = line.isAlternate;

      // Skip if this book has no over odds for this line
      if (overOdds === null || overOdds === undefined) {
        return;
      }

      // Skip if odds are -200 or worse (only consider odds > -200)
      if (overOdds <= -200) {
        return;
      }

      // Track highest point MAINLINE as fallback when no edges exist (only if odds > -200)
      // Don't use alternates as fallback - only show alternates if they have real edges
      if (!isAlternate && bookPoint > highestPoint) {
        highestPoint = bookPoint;
        highestPointMainLine = {
          overOdds,
          bookPoint,
          bestBookPrice,
          bestBookDiff
        };
      }

      // Check if this OVER has an edge
      // Edge means this book's over equals the best price AND has a meaningful diff
      if (overOdds === bestBookPrice && bestBookDiff !== null && bestBookDiff !== undefined && bestBookDiff > bestEdgeDiff) {
        bestEdgeDiff = bestBookDiff;
        bestEdgeOverLine = {
          odds: overOdds,
          point: bookPoint,
          side: 'over',
          bestBookPrice,
          bestBookDiff
        };
      }
    });

    // If we found an over with edge, return it
    if (bestEdgeOverLine) {
      return bestEdgeOverLine;
    }

    // Otherwise, return the highest point mainline over for this book
    if (highestPointMainLine) {
      const { overOdds, bookPoint, bestBookPrice, bestBookDiff } = highestPointMainLine;
      return {
        odds: overOdds,
        point: bookPoint,
        side: 'over',
        bestBookPrice,
        bestBookDiff
      };
    }

    return null;
  };

  // NFL-specific live status formatter
  const formatNFLLiveStatus = (currentStatus, game, safeGame) => {
    const period = game.period || safeGame.period;
    const clock = game.clock || safeGame.clock;
    
    // Format quarter display
    let quarterDisplay = '';
    if (!period) {
      // No period data, just show LIVE
      return (
        <span style={{ 
          backgroundColor: 'red', 
          color: 'white', 
          padding: '2px 6px', 
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '0.75rem',
          display: 'inline-block'
        }}>
          LIVE
        </span>
      );
    } else if (period === 1) {
      quarterDisplay = '1st';
    } else if (period === 2) {
      quarterDisplay = '2nd';
    } else if (period === 3) {
      quarterDisplay = '3rd';
    } else if (period === 4) {
      quarterDisplay = '4th';
    } else if (period === 5) {
      quarterDisplay = 'OT';
    } else if (period > 5) {
      quarterDisplay = `OT${period - 4}`;
    }
    
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontSize: '1.4rem',
        fontWeight: '600',
        color: 'yellow'
      }}>
        <div>{quarterDisplay}</div>
        {clock && <div style={{ fontSize: '1.3rem', fontWeight: '500' }}>{clock}</div>}
      </div>
    );
  };

  // Check if data is empty or loading
  if (!displayData || displayData.length === 0) {
    if (showLoading) {
      return (
        <div className="table-group">
          <GameHeader
            gameTime={gameTime}
            gameStatus={gameStatus}
            game={game}
            getTeamLogoUrl={getTeamLogoUrl}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            onCollapseToggle={onCollapseToggle}
            formatLiveStatus={formatNFLLiveStatus}
            showScores={true}
            isLoading={showLoading}
          />
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="table-group">
        <GameHeader 
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNFLLiveStatus}
          showScores={true}
        />
        <div className="text-center p-3 text-muted">
          {(game?.gameIDmlb?.startsWith('locked_') || game?.gameId?.startsWith('locked_')) ? (
            <UpgradeButton onClick={onSettingsClick} />
          ) : (
            'No data available for this market'
          )}
        </div>
      </div>
    );
  }

  // Filter data based on showTiers setting and master tier filter
  let filteredData = displayData;

  // Debug: Log the data for TDS market
  if (selectedMarket === 'player_tds_over') {
  }

  // Check if this is a yards market (tier filtering should not apply)
  const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_rush_attempts', 'player_reception_yds', 'player_receptions', 'player_rush_reception_yds', 'player_field_goals'].includes(selectedMarket);

  // Binary markets have yes/no outcomes, not over/under with point values
  const isBinaryMarket = selectedMarket === 'player_1st_td' || selectedMarket === 'player_anytime_td' || selectedMarket === 'player_last_td' || selectedMarket.includes('scorer') || selectedMarket.includes('first_basket');

  // Apply tier filtering (but NOT for yards markets or binary markets)
  if (isYardsMarket || isBinaryMarket) {
    // For yards markets and binary markets, show all lines
    filteredData = filteredData;
  } else if (showTiers) {
    // When tiers toggle is ON, show all tiers regardless of dropdown selection
    filteredData = filteredData;
  } else if (masterTierFilter !== null) {
    // When tiers toggle is OFF, filter to the selected tier (defaults to 0.5)
    filteredData = filteredData.filter(player => {
      return player.point === parseFloat(masterTierFilter);
    });
  } else if (!['h2h', 'totals', 'spreads'].includes(selectedMarket)) {
    // Fallback: For TDS and other player markets, show only 0.5 tier
    filteredData = filteredData.filter(player => {
      // For TDS markets specifically, only show 0.5 when tiers are off
      if (selectedMarket === 'player_tds_over' ||
          selectedMarket === 'player_pass_tds') {
        return player.point === 0.5;
      }
      // For other markets, show point <= 1
      return !player.point || player.point <= 1;
    });
  }
  
  // Debug: Log filtered data
  if (selectedMarket === 'player_tds_over') {
  }

  // Sort the filtered data for NFL (copied from original DataTable)
  const sortedData = [...filteredData].sort((a, b) => {
    // For NFL, sort by team first
    const getPlayerTeam = (player) => {
      // Direct team field
      if (player.team) {
        return player.team;
      }
      
      // Team abbreviation field
      if (player.teamAbbreviation) {
        // Check if it matches away or home team
        if (player.teamAbbreviation === safeGame.away) return 'away';
        if (player.teamAbbreviation === safeGame.home) return 'home';
        return player.teamAbbreviation;
      }
      
      // Fallback: try to infer from team name or other fields
      return 'unknown';
    };
    
    const teamA = getPlayerTeam(a);
    const teamB = getPlayerTeam(b);
    
    // Sort by team (away team first)
    if (teamA !== teamB) {
      if (teamA === safeGame.away) return -1;
      if (teamB === safeGame.away) return 1;
      if (teamA === safeGame.home) return -1;
      if (teamB === safeGame.home) return 1;
      // If neither is away/home, sort alphabetically by team
      return teamA.localeCompare(teamB);
    }
    
    // Within same team, sort by position first
    const getPlayerPosition = (player) => {
      // Get position abbreviation for sorting
      const position = player.position?.abbreviation || player.positionCode;
      if (!position) return 'ZZ'; // Put players without positions at the end
      
      // Define NFL position priority order
      const positionOrder = {
        // Offense
        'QB': 1,
        'RB': 2, 'FB': 3,
        'WR': 4, 'TE': 5,
        'T': 6, 'G': 7, 'C': 8,
        'OT': 6, 'OG': 7, 'OL': 8,
        
        // Defense  
        'DE': 10, 'DT': 11, 'NT': 12,
        'OLB': 13, 'MLB': 14, 'ILB': 15, 'LB': 16,
        'CB': 17, 'S': 18, 'FS': 19, 'SS': 20,
        'DB': 21,
        
        // Special Teams
        'K': 25, 'P': 26, 'LS': 27,
        'D/ST': 30, 'DEF': 30
      };
      
      return positionOrder[position] || 99; // Unknown positions go to end
    };
    
    const positionA = getPlayerPosition(a);
    const positionB = getPlayerPosition(b);
    
    if (positionA !== positionB) {
      return positionA - positionB;
    }
    
    // Within same team and position, sort by name
    const nameA = a.name || '';
    const nameB = b.name || '';
    const nameComparison = nameA.localeCompare(nameB);
    
    // If names are the same, sort by point value
    if (nameComparison === 0) {
      const pointA = a.point || 0;
      const pointB = b.point || 0;
      return pointA - pointB;
    }
    
    return nameComparison;
  });

  // Handle mainline markets (h2h, totals, spreads) first - copied from original DataTable logic
  if (['h2h', 'totals', 'spreads'].includes(selectedMarket)) {
    // Separate data by market type when h2h is selected (copied from original DataTable)
    let h2hData = [];
    let totalsData = [];
    let spreadsData = [];
    
    if (selectedMarket === 'h2h') {
      filteredData.forEach(player => {
        if (player.playerId?.startsWith('totals_')) {
          totalsData.push(player);
        } else if (player.playerId?.startsWith('spreads_')) {
          spreadsData.push(player);
        } else {
          h2hData.push(player);
        }
      });
      
      // Sort each dataset separately (copied from original DataTable)
      h2hData.sort((a, b) => {
        if (a.team === 'away' && b.team === 'home') return -1;
        if (a.team === 'home' && b.team === 'away') return 1;
        return 0;
      });
      
      // Don't consolidate totals - keep all point values to find the right one per bookmaker
      // Group by point value to make lookup easier
      const totalsByPoint = {};
      totalsData.forEach(d => {
        if (!totalsByPoint[d.point]) {
          totalsByPoint[d.point] = { over: null, under: null };
        }
        if (d.betType === 'over') {
          totalsByPoint[d.point].over = d;
        } else if (d.betType === 'under') {
          totalsByPoint[d.point].under = d;
        }
      });
      
      // Consolidate spreads data - one row per team
      const consolidatedSpreads = [];
      ['away', 'home'].forEach(team => {
        const consolidatedRow = { team, playerId: `spreads_${team}` };
        
        // For each bookmaker, find the best available line
        bookLogos.forEach(book => {
          const availableLines = spreadsData.filter(d => 
            d.team === team && d[`spreads_${book}`] !== null && d[`spreads_${book}`] !== undefined
          );
          
          if (availableLines.length > 0) {
            // Use the first available line (could be improved to pick best odds)
            const bestLine = availableLines[0];
            consolidatedRow[`spreads_${book}`] = bestLine[`spreads_${book}`];
            consolidatedRow.point = bestLine.point;
          }
        });
        
        consolidatedSpreads.push(consolidatedRow);
      });
      spreadsData = consolidatedSpreads;
    } else {
      // For direct totals or spreads market selection
      if (selectedMarket === 'totals') {
        totalsData = filteredData.filter(p => p.playerId?.startsWith('totals_'));
      } else if (selectedMarket === 'spreads') {
        spreadsData = filteredData.filter(p => p.playerId?.startsWith('spreads_'));
      }
    }
    
    // If no relevant data found, show message
    if (h2hData.length === 0 && totalsData.length === 0 && spreadsData.length === 0) {
      return (
        <div className="text-center p-3 text-muted">
          No {selectedMarket} data available for this game
        </div>
      );
    }

    // Use the exact same table rendering as original DataTable (copied and adapted)
    return (
      <div className="table-group">
        <GameHeader 
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNFLLiveStatus}
          showScores={true}
        />

        
        {/* Desktop: Split table design for h2h/mainlines market - show all three tables */}
        {!isMobile && selectedMarket === 'h2h' && (h2hData.length > 0 || totalsData.length > 0 || spreadsData.length > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Moneyline Table */}
            {h2hData.length > 0 && (
              <H2hAllTable
                h2hData={h2hData}
                bookLogos={bookLogos}
                getBookLogoUrl={getBookLogoUrl}
                getCellStyle={getCellStyle}
                getTooltipText={getTooltipText}
                onOddsClick={onOddsClick}
                game={safeGame}
                oddsCellStyle={oddsCellStyle}
                periodsToShow={['Game']}
              />
            )}

            {/* Totals Table */}
            {totalsData.length > 0 && (
              <TotalsTable 
                totalsData={totalsData}
                bookLogos={bookLogos}
                getBookLogoUrl={getBookLogoUrl}
                getCellStyle={getCellStyle}
                getTooltipText={getTooltipText}
                onOddsClick={onOddsClick}
                game={safeGame}
                oddsCellStyle={oddsCellStyle}
              />
            )}

            {/* Spreads Table */}
            {spreadsData.length > 0 && (
              <SpreadsTable 
                spreadsData={spreadsData}
                bookLogos={bookLogos}
                getBookLogoUrl={getBookLogoUrl}
                getCellStyle={getCellStyle}
                getTooltipText={getTooltipText}
                onOddsClick={onOddsClick}
                game={safeGame}
                oddsCellStyle={oddsCellStyle}
              />
            )}
          </div>
        )}

        {/* Desktop: Individual totals table */}
        {!isMobile && selectedMarket === 'totals' && totalsData.length > 0 && (
          <TotalsTable 
            totalsData={totalsData}
            bookLogos={bookLogos}
            getBookLogoUrl={getBookLogoUrl}
            getCellStyle={getCellStyle}
            getTooltipText={getTooltipText}
            onOddsClick={onOddsClick}
            game={safeGame}
            oddsCellStyle={oddsCellStyle}
          />
        )}

        {/* Desktop: Individual spreads table */}
        {!isMobile && selectedMarket === 'spreads' && spreadsData.length > 0 && (
          <SpreadsTable 
            spreadsData={spreadsData}
            bookLogos={bookLogos}
            getBookLogoUrl={getBookLogoUrl}
            getCellStyle={getCellStyle}
            getTooltipText={getTooltipText}
            onOddsClick={onOddsClick}
            game={safeGame}
            oddsCellStyle={oddsCellStyle}
          />
        )}

        {/* Mobile: Show appropriate table based on selected market */}
        {isMobile && (
          <>
            {console.log('NFL Mobile check:', { isMobile, selectedMarket, h2hCount: h2hData.length, totalsCount: totalsData.length, spreadsCount: spreadsData.length })}
            {selectedMarket === 'h2h' && (h2hData.length > 0 || totalsData.length > 0 || spreadsData.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <H2hMiniTable
                  h2hData={h2hData}
                  getBookLogoUrl={getBookLogoUrl}
                  getTeamLogoUrl={getTeamLogoUrlDirect}
                  getCellStyle={getCellStyle}
                  getTooltipText={getTooltipText}
                  onOddsClick={onOddsClick}
                  game={safeGame}
                  periodsToShow={['Game']}
                />
                <TotalsMiniTable
                  totalsData={totalsData}
                  getBookLogoUrl={getBookLogoUrl}
                  getCellStyle={getCellStyle}
                  getTooltipText={getTooltipText}
                  onOddsClick={onOddsClick}
                  game={safeGame}
                  periodsToShow={['Game']}
                />
                <SpreadsMiniTable
                  spreadsData={spreadsData}
                  getBookLogoUrl={getBookLogoUrl}
                  getTeamLogoUrl={getTeamLogoUrlDirect}
                  getCellStyle={getCellStyle}
                  getTooltipText={getTooltipText}
                  onOddsClick={onOddsClick}
                  game={safeGame}
                  periodsToShow={['Game']}
                />
              </div>
            )}

            {selectedMarket === 'totals' && totalsData.length > 0 && (
              <TotalsTable 
                totalsData={totalsData}
                bookLogos={bookLogos}
                getBookLogoUrl={getBookLogoUrl}
                getCellStyle={getCellStyle}
                getTooltipText={getTooltipText}
                onOddsClick={onOddsClick}
                game={safeGame}
                oddsCellStyle={oddsCellStyle}
              />
            )}

            {selectedMarket === 'spreads' && spreadsData.length > 0 && (
              <SpreadsTable 
                spreadsData={spreadsData}
                bookLogos={bookLogos}
                getBookLogoUrl={getBookLogoUrl}
                getCellStyle={getCellStyle}
                getTooltipText={getTooltipText}
                onOddsClick={onOddsClick}
                game={safeGame}
                oddsCellStyle={oddsCellStyle}
              />
            )}
          </>
        )}
      </div>
    );
  }

  // Process data for NFL player props
  // For yards markets and receptions, handle the two-row structure (over/under)

  if (safeGame.gameIDmlb === '401772822') {
    console.log('NFLDataTable rendering path decision:', {
      selectedMarket,
      isYardsMarket,
      willTakeYardsPath: isYardsMarket,
      willTakeTeamGroupingPath: !isYardsMarket
    });
  }

  if (isYardsMarket) {
    // Yards markets - group all point values for each player
    // Each book can have different lines
    const playersByName = {};
    sortedData.forEach(player => {
      const key = player.name || player.playerId;
      if (!playersByName[key]) {
        playersByName[key] = {
          name: player.name,
          playerId: player.playerId,
          team: player.team,
          teamId: player.teamId,
          position: player.position,
          positionCode: player.positionCode,
          lines: []
        };
      }
      // Add this line/point value to the player's collection
      playersByName[key].lines.push(player);
    });
    
    // For each player, find the main line for each book
    const groupedData = {};
    Object.entries(playersByName).forEach(([name, playerData]) => {
      groupedData[name] = {
        name: playerData.name,
        playerId: playerData.playerId,
        team: playerData.team,
        teamId: playerData.teamId,
        position: playerData.position,
        positionCode: playerData.positionCode,
        // Store all lines so we can find the right one for each book
        lines: playerData.lines
      };
    });

    // Edge View - single row per player with best edge line for each book
    if (nflViewMode === 'bestline') {
      return (
        <div className="table-group">
          <GameHeader
            gameTime={gameTime}
            gameStatus={gameStatus}
            game={game}
            getTeamLogoUrl={getTeamLogoUrl}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            onCollapseToggle={onCollapseToggle}
            formatLiveStatus={formatNFLLiveStatus}
            showScores={true}
          />

          <div className="table-container">
            <Table bordered size="sm" className="text-center">
            <thead>
              <tr>
                <th style={{ width: '200px' }}>NAME</th>
                {bookLogos.map(book => (
                  <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                    <img
                      src={getBookLogoUrl(book)}
                      alt={`${book} logo`}
                      title={book}
                      style={{ height: '20px' }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                let isEven = true;

                return Object.values(groupedData).map((playerData, index) => {
                  // Determine color based on even/odd
                  const bgColor = isEven ? 'rgb(26, 26, 26)' : 'rgb(45, 45, 45)';
                  isEven = !isEven; // Toggle for next player

                  return (
                    <tr key={`${playerData.name}-${index}`} style={{ backgroundColor: bgColor }}>
                      <td style={{
                        textAlign: 'left',
                        padding: '8px',
                        backgroundColor: bgColor
                      }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <img
                        src={(() => {
                          const teamIdentifier = playerData.teamId || playerData.team;

                          // Use direct logo URLs from game data when possible
                          if (playerData.team === 'away') {
                            return safeGame.awayLogo || getTeamLogoUrlDirect(safeGame.away, false, 'nfl');
                          } else if (playerData.team === 'home') {
                            return safeGame.homeLogo || getTeamLogoUrlDirect(safeGame.home, false, 'nfl');
                          } else if (playerData.team === safeGame.away) {
                            return safeGame.awayLogo || getTeamLogoUrlDirect(safeGame.away, false, 'nfl');
                          } else if (playerData.team === safeGame.home) {
                            return safeGame.homeLogo || getTeamLogoUrlDirect(safeGame.home, false, 'nfl');
                          }

                          // Fallback
                          return getTeamLogoUrlDirect(teamIdentifier, false, 'nfl');
                        })()}
                        alt={playerData.teamId || playerData.team}
                        style={{ width: '16px', height: '16px' }}
                        onError={(e) => {
                          e.target.style.visibility = 'hidden';
                          e.target.style.width = '0px';
                          e.target.style.height = '0px';
                          e.target.style.margin = '0px';
                        }}
                      />
                      <span
                        style={{ cursor: 'pointer' }}
                        onContextMenu={handleNameContextMenu(playerData)}
                        onClick={() => {
                          if (onPlayerClick) {
                            onPlayerClick(
                              safeGame.gameId || safeGame.gameIDmlb,
                              playerData.playerId,
                              playerData.name,
                              selectedMarket,
                              safeGame.away,
                              safeGame.home,
                              playerData.team || playerData.teamId,
                              playerData.positionCode || playerData.position?.abbreviation
                            );
                          }
                        }}
                      >
                        {formatPlayerName(playerData.name)}
                      </span>
                      {(playerData.positionCode || playerData.position?.abbreviation) && (
                        <span style={{
                          fontSize: '0.75em',
                          color: '#9CA3AF',
                          fontWeight: 'normal'
                        }}>
                          ({playerData.positionCode || playerData.position?.abbreviation})
                        </span>
                      )}
                    </div>
                  </td>
                  {bookLogos.map(book => {
                    // Find the best edge line for this book
                    const bestLine = getBestLineByEdge(playerData.lines, book, selectedMarket);

                    if (!bestLine) {
                      return (
                        <td key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                          <span style={{ color: '#6B7280' }}>-</span>
                        </td>
                      );
                    }

                    const { odds, point, side, bestBookPrice, bestBookDiff } = bestLine;

                    if (odds === null || odds === undefined) {
                      return (
                        <td key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                          <span style={{ color: '#6B7280' }}>-</span>
                        </td>
                      );
                    }

                    // Apply tier styling
                    const cellStyle = getCellStyle(odds, bestBookPrice, bestBookDiff, bookLogos.length, null, playerData.lines, book, selectedMarket);
                    const tooltip = getTooltipText(odds, bestBookPrice, bestBookDiff, bookLogos.length);

                    return (
                      <td
                        key={`${selectedMarket}-${book}`}
                        className={getCellClassName(safeGame.gameId || safeGame.gameIDmlb, playerData.playerId, selectedMarket, book, point, side)}
                        onClick={() => {
                          if (onOddsClick) {
                            onOddsClick(
                              safeGame.gameId || safeGame.gameIDmlb,
                              playerData.playerId,
                              playerData.name,
                              selectedMarket,
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              playerData.team,
                              playerData.position,
                              point,
                              side
                            );
                          }
                        }}
                        title={tooltip || ''}
                        style={{
                          textAlign: 'center',
                          padding: '4px',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ ...cellStyle, padding: '4px', borderRadius: '3px' }}>
                          <div style={{ fontSize: '0.75rem' }}>
                            {side === 'over' ? `o${point}` : `u${point}`}
                          </div>
                          <div style={{ fontWeight: 'bold' }}>
                            {odds > 0 ? `+${odds}` : odds}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                    </tr>
                  );
                });
              })()}
            </tbody>
          </Table>
          </div>
        </div>
      );
    }

    // Middles View — one container per player, each holding every valid
    // (over × under) middle combination as a horizontal card row. Each side
    // must be ≥ -120 American; over point must be strictly less than under
    // point. Eligible lines pass an empirical mainline gate (any book
    // quoting an under). All books tied at the best odds for a given
    // point/side share a card.
    if (nflViewMode === 'middles') {
      const priceToProfit = (o) => {
        const n = Number(o);
        if (!Number.isFinite(n) || n === 0) return 0;
        return n > 0 ? n / 100 : 100 / Math.abs(n);
      };

      // Only books the user enabled for Best Bets are eligible for middles.
      // Keeps the calculation aligned with their preferred sportsbook set.
      const middlesBooks = bookLogos.filter(b => isBookEnabledForBestBets(b));

      // For a player, collect the best over odds at each point and the best
      // under odds at each point, keyed by point value. Then enumerate every
      // (over × under) pair where under.point > over.point. Lines/books
      // flagged with _isAvailable === false are excluded — an unavailable
      // price can't complete a middle bet.
      const collectSideByPoint = (eligibleLines) => {
        const overByPoint = new Map();
        const underByPoint = new Map();
        eligibleLines.forEach(line => {
          middlesBooks.forEach(book => {
            const point = line[`${selectedMarket}_${book}_point`] ?? line.point;
            if (point == null) return;
            const bookAvail = line[`${selectedMarket}_${book}_isAvailable`] !== false;
            const overAvail = bookAvail && line[`${selectedMarket}_${book}_over_isAvailable`] !== false;
            const underAvail = bookAvail && line[`${selectedMarket}_${book}_under_isAvailable`] !== false;
            const oOdds = line[`${selectedMarket}_${book}_over`];
            const uOdds = line[`${selectedMarket}_${book}_under`];
            if (overAvail && oOdds !== null && oOdds !== undefined && Number(oOdds) >= -120) {
              const cur = overByPoint.get(point);
              if (!cur || Number(oOdds) > Number(cur.odds)) {
                overByPoint.set(point, { point, odds: oOdds, books: [book] });
              } else if (Number(oOdds) === Number(cur.odds) && !cur.books.includes(book)) {
                cur.books.push(book);
              }
            }
            if (underAvail && uOdds !== null && uOdds !== undefined && Number(uOdds) >= -120) {
              const cur = underByPoint.get(point);
              if (!cur || Number(uOdds) > Number(cur.odds)) {
                underByPoint.set(point, { point, odds: uOdds, books: [book] });
              } else if (Number(uOdds) === Number(cur.odds) && !cur.books.includes(book)) {
                cur.books.push(book);
              }
            }
          });
        });
        return { overs: [...overByPoint.values()], unders: [...underByPoint.values()] };
      };

      const playerCards = Object.values(groupedData).map((playerData) => {
        if (!playerData.lines || playerData.lines.length === 0) return null;
        const eligibleLines = playerData.lines.filter(line => {
          if (line.isAlternate) return false;
          for (const b of middlesBooks) {
            const u = line[`${selectedMarket}_${b}_under`];
            const uAvail = line[`${selectedMarket}_${b}_isAvailable`] !== false && line[`${selectedMarket}_${b}_under_isAvailable`] !== false;
            if (u !== null && u !== undefined && uAvail) return true;
          }
          return false;
        });
        if (eligibleLines.length === 0) return null;

        const { overs, unders } = collectSideByPoint(eligibleLines);

        const middles = [];
        overs.forEach(o => {
          unders.forEach(u => {
            if (u.point <= o.point) return;
            // A real middle needs at least one book on the over side and a
            // DIFFERENT book on the under side. If the union of tied books
            // on both sides is a single book (e.g. DK on both), the same
            // book can't be used for both bets — skip it.
            const bookUnion = new Set([...o.books, ...u.books]);
            if (bookUnion.size < 2) return;
            const pO = priceToProfit(o.odds);
            const pU = priceToProfit(u.odds);
            middles.push({
              bestOver: o,
              bestUnder: u,
              maxProfit: pO + pU,
              maxLoss: Math.min(pO, pU) - 1,
              width: u.point - o.point,
            });
          });
        });
        if (middles.length === 0) return null;

        // Highest max profit first, break ties by widest middle
        middles.sort((a, b) => (b.maxProfit - a.maxProfit) || (b.width - a.width));
        return { playerData, middles };
      }).filter(Boolean);

      // Sort players by their best (highest max profit) middle
      playerCards.sort((a, b) => (b.middles[0].maxProfit - a.middles[0].maxProfit));

      const fmtOdds = (o) => (Number(o) > 0 ? `+${o}` : `${o}`);
      const fmtUnit = (n) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}u`;

      return (
        <div className="table-group">
          <GameHeader
            gameTime={gameTime}
            gameStatus={gameStatus}
            game={game}
            getTeamLogoUrl={getTeamLogoUrl}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            onCollapseToggle={onCollapseToggle}
            formatLiveStatus={formatNFLLiveStatus}
            showScores={true}
          />
          {!isCollapsed && (
            <div style={{ padding: '10px 12px' }}>
              {playerCards.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: '0.85rem', padding: '12px', textAlign: 'center' }}>
                  No qualifying middle opportunities.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {playerCards.map(({ playerData, middles }) => {
                    const teamLogo =
                      playerData.team === 'away' ? (safeGame.awayLogo || getTeamLogoUrlDirect(safeGame.away, false, 'nfl'))
                      : playerData.team === 'home' ? (safeGame.homeLogo || getTeamLogoUrlDirect(safeGame.home, false, 'nfl'))
                      : playerData.team === safeGame.away ? (safeGame.awayLogo || getTeamLogoUrlDirect(safeGame.away, false, 'nfl'))
                      : playerData.team === safeGame.home ? (safeGame.homeLogo || getTeamLogoUrlDirect(safeGame.home, false, 'nfl'))
                      : getTeamLogoUrlDirect(playerData.teamId || playerData.team, false, 'nfl');
                    const pos = playerData.positionCode || playerData.position?.abbreviation || '';

                    return (
                      <div
                        key={`middle-${playerData.name}`}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          padding: '10px 14px',
                          backgroundColor: 'rgba(255,255,255,0.03)',
                          border: '1px solid #2a2d31',
                          borderRadius: '6px',
                          alignItems: 'flex-start',
                        }}
                      >
                        {/* Left column: player info (fixed) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '180px', maxWidth: '180px', flexShrink: 0 }}>
                          <img
                            src={teamLogo}
                            alt=""
                            style={{ width: 22, height: 22, flexShrink: 0 }}
                            onError={(e) => { e.target.style.visibility = 'hidden'; }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div
                              onClick={() => {
                                if (onPlayerClick) {
                                  onPlayerClick(
                                    safeGame.gameId || safeGame.gameIDmlb,
                                    playerData.playerId,
                                    playerData.name,
                                    selectedMarket,
                                    safeGame.away,
                                    safeGame.home,
                                    playerData.team || playerData.teamId,
                                    pos
                                  );
                                }
                              }}
                              style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {formatPlayerName(playerData.name)}
                            </div>
                            <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                              {(playerData.team && playerData.team !== 'away' && playerData.team !== 'home') ? playerData.team : (playerData.team === 'away' ? safeGame.away : safeGame.home)}
                              {pos ? ` · ${pos}` : ''}
                            </div>
                          </div>
                        </div>

                        {/* Right side: middle cards wrap responsively so they
                            never overflow the container. */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', flex: 1, minWidth: 0 }}>
                          {middles.map((m, idx) => (
                            <div
                              key={`m-${idx}-${m.bestOver.point}-${m.bestUnder.point}`}
                              style={{
                                display: 'flex',
                                gap: '10px',
                                padding: '6px 10px',
                                backgroundColor: 'rgba(0,0,0,0.25)',
                                border: '1px solid #333',
                                borderRadius: '6px',
                                alignItems: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {/* U / O odds stack */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(239, 68, 68, 0.12)', padding: '4px 8px', borderRadius: '4px' }}>
                                  <span style={{ color: '#f87171', fontWeight: 700, fontSize: '0.72rem', minWidth: '42px' }}>U {m.bestUnder.point}</span>
                                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>{fmtOdds(m.bestUnder.odds)}</span>
                                  <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                                    {m.bestUnder.books.map(b => (
                                      <img key={b} src={getBookLogoUrl(b)} alt={b} title={b} style={{ height: 16 }} onError={(e) => { e.target.style.display = 'none'; }} />
                                    ))}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(34, 197, 94, 0.12)', padding: '4px 8px', borderRadius: '4px' }}>
                                  <span style={{ color: '#4ade80', fontWeight: 700, fontSize: '0.72rem', minWidth: '42px' }}>O {m.bestOver.point}</span>
                                  <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>{fmtOdds(m.bestOver.odds)}</span>
                                  <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                                    {m.bestOver.books.map(b => (
                                      <img key={b} src={getBookLogoUrl(b)} alt={b} title={b} style={{ height: 16 }} onError={(e) => { e.target.style.display = 'none'; }} />
                                    ))}
                                  </div>
                                </div>
                              </div>
                              {/* Max win / loss */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', minWidth: '68px' }}>
                                <div style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: 700 }}>{fmtUnit(m.maxProfit)}</div>
                                <div style={{ color: '#9ca3af', fontSize: '0.62rem' }}>max win</div>
                                <div style={{ color: m.maxLoss >= 0 ? '#4ade80' : '#f87171', fontSize: '0.85rem', fontWeight: 700, marginTop: '3px' }}>{fmtUnit(m.maxLoss)}</div>
                                <div style={{ color: '#9ca3af', fontSize: '0.62rem' }}>max loss</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // O/U View - two-row structure (existing logic)
    return (
      <div className="table-group">
        <GameHeader
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          getTeamLogoUrl={getTeamLogoUrl}
          isMobile={isMobile}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          formatLiveStatus={formatNFLLiveStatus}
          showScores={true}
        />
        
        <div className="table-container">
          <Table bordered size="sm" className="text-center">
          <thead>
            <tr>
              <th style={{ width: '200px' }}>NAME</th>
              {bookLogos.map(book => (
                <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                  <img 
                    src={getBookLogoUrl(book)} 
                    alt={`${book} logo`} 
                    title={book} 
                    style={{ height: '20px' }} 
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              let isEven = true;

              return Object.values(groupedData).map((playerData, index) => {
                // Determine color based on even/odd
                const bgColor = isEven ? 'rgb(26, 26, 26)' : 'rgb(45, 45, 45)';
                isEven = !isEven; // Toggle for next player

                return (
                  <React.Fragment key={`${playerData.name}-${index}`}>
                    <tr style={{ backgroundColor: bgColor }}>
                      <td rowSpan={2} style={{ 
                        verticalAlign: 'middle', 
                        textAlign: 'left', 
                        padding: '8px',
                        backgroundColor: bgColor
                      }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <img
                        src={(() => {
                          const teamIdentifier = playerData.teamId || playerData.team;
                          
                          
                          
                          // Use direct logo URLs from game data when possible
                          if (playerData.team === 'away') {
                            return safeGame.awayLogo || getTeamLogoUrlDirect(safeGame.away, false, 'nfl');
                          } else if (playerData.team === 'home') {
                            return safeGame.homeLogo || getTeamLogoUrlDirect(safeGame.home, false, 'nfl');
                          } else if (playerData.team === safeGame.away) {
                            return safeGame.awayLogo || getTeamLogoUrlDirect(safeGame.away, false, 'nfl');
                          } else if (playerData.team === safeGame.home) {
                            return safeGame.homeLogo || getTeamLogoUrlDirect(safeGame.home, false, 'nfl');
                          }
                          
                          // Fallback for other team identifiers - use imported function with NFL mapping
                          return getTeamLogoUrlDirect(teamIdentifier, false, 'nfl');
                        })()}
                        alt={playerData.teamId || playerData.team}
                        style={{ width: '16px', height: '16px' }}
                        onError={(e) => {
                          e.target.style.visibility = 'hidden';
                          e.target.style.width = '0px';
                          e.target.style.height = '0px';
                          e.target.style.margin = '0px';
                        }}
                      />
                      <span
                        style={{ cursor: 'pointer' }}
                        onContextMenu={handleNameContextMenu(playerData)}
                        onClick={() => {
                          if (onPlayerClick) {
                            onPlayerClick(
                              safeGame.gameId || safeGame.gameIDmlb,
                              playerData.playerId,
                              playerData.name,
                              selectedMarket,
                              safeGame.away,
                              safeGame.home,
                              playerData.team || playerData.teamId,
                              playerData.positionCode || playerData.position?.abbreviation
                            );
                          }
                        }}
                      >
                        {formatPlayerName(playerData.name)}
                      </span>
                      {(playerData.positionCode || playerData.position?.abbreviation) && (
                        <span style={{
                          fontSize: '0.75em',
                          color: '#9CA3AF',
                          fontWeight: 'normal'
                        }}>
                          ({playerData.positionCode || playerData.position?.abbreviation})
                        </span>
                      )}
                    </div>
                  </td>
                  {bookLogos.map(book => {
                    // Find the main line that has BOTH over and under for this book (non-alternate)
                    // Prioritize non-alternates first
                    const lineWithOdds = playerData.lines?.find(line =>
                      !line.isAlternate &&
                      line[`${selectedMarket}_${book}_over`] !== null &&
                      line[`${selectedMarket}_${book}_over`] !== undefined &&
                      line[`${selectedMarket}_${book}_under`] !== null &&
                      line[`${selectedMarket}_${book}_under`] !== undefined
                    ) || playerData.lines?.find(line =>
                      // Fallback to any line with over if no mainline has both
                      !line.isAlternate &&
                      line[`${selectedMarket}_${book}_over`] !== null &&
                      line[`${selectedMarket}_${book}_over`] !== undefined
                    ) || playerData;
                    
                    const overOdds = lineWithOdds[`${selectedMarket}_${book}_over`];
                    const point = lineWithOdds[`${selectedMarket}_${book}_point`] || lineWithOdds.point;
                    const bestBookPrice = lineWithOdds[`${selectedMarket}_bestBookPrice`];
                    const bestBookDiff = lineWithOdds[`${selectedMarket}_bestBookDiff`];

                    return (
                      <td
                        key={`${selectedMarket}-${book}-over`}
                        className={getCellClassName(safeGame.gameId || safeGame.gameIDmlb, playerData.playerId, selectedMarket, book, point, 'over')}
                        onClick={() => {
                          if (overOdds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameId || safeGame.gameIDmlb,
                              playerData.playerId,
                              playerData.name,
                              selectedMarket,
                              book,
                              overOdds,
                              safeGame.away,
                              safeGame.home,
                              playerData.team,
                              playerData.position,
                              point,
                              'over'
                            );
                          }
                        }}
                        style={{
                          textAlign: 'center',
                          padding: '4px 4px 0px 4px',
                          fontSize: '0.85rem',
                          cursor: overOdds ? 'pointer' : 'default',
                          borderBottom: `1px solid ${bgColor}`,
                          opacity: lineWithOdds[`${selectedMarket}_${book}_over_isAvailable`] === false ? 0.25 : undefined
                        }}
                      >
                        {overOdds ? (() => {
                          const baseCellStyle = getCellStyle(overOdds, bestBookPrice, bestBookDiff, bookLogos.length, null, playerData.lines, book, selectedMarket);
                          return (
                            <div style={{ ...baseCellStyle, padding: '4px', borderRadius: '3px' }}>
                              <div style={{ fontWeight: 'bold' }}>{overOdds > 0 ? `+${overOdds}` : overOdds}</div>
                              <div style={{ fontSize: '0.75rem', color: baseCellStyle.backgroundColor ? 'inherit' : '#6c757d' }}>o{point}</div>
                            </div>
                          );
                        })() : null}
                      </td>
                    );
                  })}
                    </tr>
                    <tr style={{ backgroundColor: bgColor }}>
                      {bookLogos.map(book => {
                        // Use the same logic as over row - find the main line with BOTH over and under
                        // Prioritize non-alternates first
                    const lineWithOdds = playerData.lines?.find(line =>
                      !line.isAlternate &&
                      line[`${selectedMarket}_${book}_over`] !== null &&
                      line[`${selectedMarket}_${book}_over`] !== undefined &&
                      line[`${selectedMarket}_${book}_under`] !== null &&
                      line[`${selectedMarket}_${book}_under`] !== undefined
                    ) || playerData.lines?.find(line =>
                      // Fallback to any line with under if no mainline has both
                      !line.isAlternate &&
                      line[`${selectedMarket}_${book}_under`] !== null &&
                      line[`${selectedMarket}_${book}_under`] !== undefined
                    ) || playerData;
                    
                    const underOdds = lineWithOdds[`${selectedMarket}_${book}_under`];
                    const point = lineWithOdds[`${selectedMarket}_${book}_point`] || lineWithOdds.point;
                    const bestBookPrice = lineWithOdds[`${selectedMarket}_bestBookPrice`];
                    const bestBookDiff = lineWithOdds[`${selectedMarket}_bestBookDiff`];

                    return (
                      <td
                        key={`${selectedMarket}-${book}-under`}
                        className={getCellClassName(safeGame.gameId || safeGame.gameIDmlb, playerData.playerId, selectedMarket, book, point, 'under')}
                        onClick={() => {
                          if (underOdds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameId || safeGame.gameIDmlb,
                              playerData.playerId,
                              playerData.name,
                              selectedMarket,
                              book,
                              underOdds,
                              safeGame.away,
                              safeGame.home,
                              playerData.team,
                              playerData.position,
                              point,
                              'under'
                            );
                          }
                        }}
                        style={{
                          textAlign: 'center',
                          padding: '0px 4px 4px 4px',
                          fontSize: '0.85rem',
                          cursor: underOdds ? 'pointer' : 'default',
                          borderTop: `1px solid ${bgColor}`,
                          opacity: lineWithOdds[`${selectedMarket}_${book}_under_isAvailable`] === false ? 0.25 : undefined
                        }}
                      >
                        {underOdds ? (() => {
                          const baseCellStyle = getCellStyle(underOdds, bestBookPrice, bestBookDiff, bookLogos.length, null, playerData.lines, book, selectedMarket);
                          return (
                            <div style={{ ...baseCellStyle, padding: '4px', borderRadius: '3px' }}>
                              <div style={{ fontSize: '0.75rem', color: baseCellStyle.backgroundColor ? 'inherit' : '#6c757d' }}>u{point}</div>
                              <div style={{ fontWeight: 'bold' }}>{underOdds > 0 ? `+${underOdds}` : underOdds}</div>
                            </div>
                          );
                        })() : null}
                      </td>
                    );
                  })}
                    </tr>
                  </React.Fragment>
                );
              });
            })()}
          </tbody>
        </Table>
        </div>
      </div>
    );
  }

  // Standard player props (including ATTDS) - organize by team with separate tables
  const playersByTeam = {};

  if (safeGame.gameIDmlb === '401772822') {
    console.log('NFL team grouping:', {
      gameId: safeGame.gameIDmlb,
      away: safeGame.away,
      home: safeGame.home,
      totalPlayers: sortedData.length,
      firstPlayer: sortedData[0],
      selectedMarket,
      isTdsMarket: selectedMarket === 'player_tds_over',
      first3Players: sortedData.slice(0, 3).map(p => ({ name: p.name, team: p.team, teamAbv: p.teamAbv }))
    });
  }

  sortedData.forEach((player, idx) => {
    // Determine which team the player belongs to
    let teamKey = 'unknown';
    let teamName = 'Unknown Team';
    let teamLogo = null;

    // Debug first 3 players
    if (safeGame.gameIDmlb === '401772822' && idx < 3) {
      console.log('Team matching debug:', {
        playerName: player.name,
        playerTeam: player.team,
        playerTeamAbv: player.teamAbv,
        gameAway: safeGame.away,
        gameHome: safeGame.home,
        match1: player.team === safeGame.away,
        match2: player.teamAbv === safeGame.away,
        match3: player.team === safeGame.home,
        match4: player.teamAbv === safeGame.home
      });
    }

    // Try to determine team based on team abbreviation
    if (player.team === safeGame.away || player.teamAbv === safeGame.away) {
      teamKey = 'away';
      teamName = safeGame.awayTeamFull || safeGame.away;
      teamLogo = safeGame.awayLogo || getTeamLogoUrl(safeGame.away);
    } else if (player.team === safeGame.home || player.teamAbv === safeGame.home) {
      teamKey = 'home';
      teamName = safeGame.homeTeamFull || safeGame.home;
      teamLogo = safeGame.homeLogo || getTeamLogoUrl(safeGame.home);
    } else {
      // Fallback for players without team (defense, no scorer, etc.)
      teamKey = player.team || 'unknown';
      teamName = player.team || 'Unknown Team';
      teamLogo = player.team ? getTeamLogoUrlDirect(player.team, false, 'nfl') : null;
    }
    
    if (!playersByTeam[teamKey]) {
      playersByTeam[teamKey] = {
        name: teamName,
        logo: teamLogo,
        players: []
      };
    }
    
    playersByTeam[teamKey].players.push(player);
  });

  if (safeGame.gameIDmlb === '401772822') {
    console.log('NFL playersByTeam result:', {
      teams: Object.keys(playersByTeam),
      teamCounts: Object.entries(playersByTeam).map(([k, v]) => `${k}: ${v.players.length}`)
    });
  }

  return (
    <div className="table-group">
      <GameHeader 
        gameTime={gameTime}
        gameStatus={gameStatus}
        game={game}
        getTeamLogoUrl={getTeamLogoUrl}
        isMobile={isMobile}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
        formatLiveStatus={formatNFLLiveStatus}
        showScores={true}
      />
      
      <div className="table-container">
        {Object.entries(playersByTeam).map(([teamKey, teamData], teamIndex) => (
        <div key={teamKey} style={{ marginBottom: teamIndex < Object.keys(playersByTeam).length - 1 ? '24px' : '0' }}>
          <Table bordered size="sm" className="text-center">
            <thead>
              <tr>
                <th style={{ width: '200px' }}>NAME</th>
                {bookLogos.map(book => (
                  <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                    <img
                      src={getBookLogoUrl(book)}
                      alt={`${book} logo`}
                      title={book}
                      style={{ height: '20px' }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teamData.players.map((player, index) => {
                const isFirstRowForPlayer = index === 0 || teamData.players[index - 1].name !== player.name;

                // Debug for first 3 players of first team
                if (safeGame.gameIDmlb === '401772822' && teamIndex === 0 && index < 3) {
                  console.log('Player row debug:', {
                    index,
                    name: player.name,
                    isFirstRowForPlayer,
                    prevPlayerName: index > 0 ? teamData.players[index - 1].name : 'N/A',
                    teamLogo: teamData.logo,
                    teamKey
                  });
                }

                return (
                  <tr key={player.tierKey || player.id || `${player.name}-${index}`}>
                    <td style={{ textAlign: 'left', padding: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isFirstRowForPlayer && (
                            <>
                              <img
                                src={teamData.logo?.startsWith('http') ? teamData.logo : getTeamLogoUrlDirect(teamData.logo || teamKey, false, 'nfl')}
                                alt={teamData.logo || teamKey}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => {
                                  e.target.style.visibility = 'hidden';
                                  e.target.style.width = '0px';
                                  e.target.style.height = '0px';
                                  e.target.style.margin = '0px';
                                }}
                              />
                              <span
                                style={{ cursor: 'pointer' }}
                                onContextMenu={handleNameContextMenu(player)}
                                onClick={() => {
                                  if (onPlayerClick) {
                                    onPlayerClick(
                                      safeGame.gameId || safeGame.gameIDmlb,
                                      player.playerId,
                                      player.name,
                                      selectedMarket,
                                      safeGame.away,
                                      safeGame.home,
                                      player.team || player.teamId,
                                      typeof player.positionCode === 'string' ? player.positionCode : player.position?.abbreviation
                                    );
                                  }
                                }}
                              >
                                {formatPlayerName(player.name)}
                              </span>
                              {(() => {
                                // Safely extract position abbreviation (handle both string and object)
                                const positionText = typeof player.positionCode === 'string'
                                  ? player.positionCode
                                  : player.position?.abbreviation;
                                return positionText ? (
                                  <span style={{
                                    fontSize: '0.75em',
                                    color: '#9CA3AF',
                                    fontWeight: 'normal'
                                  }}>
                                    ({positionText})
                                  </span>
                                ) : null;
                              })()}
                            </>
                          )}
                        </div>
                        {/* Always show point value for each tier - aligned to the right */}
                        {player.point !== null && player.point !== undefined && selectedMarket !== 'h2h' && selectedMarket !== 'player_anytime_td' && (
                          <small style={{
                            color: '#6B7280',
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                            flexShrink: 0,
                            minWidth: '60px',
                            textAlign: 'right'
                          }}>
                            {player.point === 'yes' ? 'Yes' :
                             player.point === 'no' ? 'No' :
                             isMobile ? player.point : `Over ${player.point}`}
                          </small>
                        )}
                      </div>
                    </td>
                  {bookLogos.map(book => {
                    // For over/under markets, append _over to get the correct field
                    // Binary markets (first TD, anytime TD, etc.) use direct field name
                    const isOverUnderMarket = selectedMarket.endsWith('_over') ||
                                             selectedMarket.endsWith('_under') ||
                                             selectedMarket === 'player_pass_tds' ||
                                             selectedMarket === 'player_rush_tds';
                    const oddsField = isOverUnderMarket
                      ? `${selectedMarket}_${book}_over`
                      : `${selectedMarket}_${book}`;
                    const odds = player[oddsField];
                    const point = player.point;
                    
                    return (
                      <td
                        key={`${selectedMarket}-${book}`}
                        className={getCellClassName(safeGame.gameId || safeGame.gameIDmlb, player.playerId, selectedMarket, book, point, 'yes')}
                        onClick={() => {
                          if (odds && onOddsClick) {
                            onOddsClick(
                              safeGame.gameId || safeGame.gameIDmlb,
                              player.playerId,
                              player.name,
                              selectedMarket,
                              book,
                              odds,
                              safeGame.away,
                              safeGame.home,
                              player.team,
                              typeof player.positionCode === 'string' ? player.positionCode : player.position?.abbreviation,
                              point, // Pass the touchdown line (0.5, 1.5, etc.)
                              'yes' // ATTDS is a yes/no market
                            );
                          }
                        }}
                        style={{
                          ...getCellStyle(
                            odds,
                            player[`${selectedMarket}_bestBookPrice`],
                            // Don't show edges when multiple books have the same best price (data error)
                            player[`${selectedMarket}_bestBook`] === 'multi' ? 0 : player[`${selectedMarket}_bestBookDiff`],
                            bookLogos.filter(b => {
                              const isOverUnder = selectedMarket.endsWith('_over') ||
                                                 selectedMarket.endsWith('_under') ||
                                                 selectedMarket === 'player_pass_tds' ||
                                                 selectedMarket === 'player_rush_tds';
                              const checkField = isOverUnder
                                ? `${selectedMarket}_${b}_over`
                                : `${selectedMarket}_${b}`;
                              return player[checkField] !== null && player[checkField] !== undefined;
                            }).length,
                            player[`${selectedMarket}_${book}_direction`]
                          ),
                          textAlign: 'center',
                          padding: '8px 4px',
                          cursor: odds ? 'pointer' : 'default',
                          opacity: player[`${oddsField}_isAvailable`] === false ? 0.25 : undefined
                        }}
                        title={getTooltipText(
                          odds,
                          player[`${selectedMarket}_bestBookPrice`],
                          player[`${selectedMarket}_bestBook`] === 'multi' ? 0 : player[`${selectedMarket}_bestBookDiff`],
                          bookLogos.filter(b => {
                            const isOverUnder = selectedMarket.endsWith('_over') ||
                                               selectedMarket.endsWith('_under') ||
                                               selectedMarket === 'player_pass_tds' ||
                                               selectedMarket === 'player_rush_tds';
                            const checkField = isOverUnder
                              ? `${selectedMarket}_${b}_over`
                              : `${selectedMarket}_${b}`;
                            return player[checkField] !== null && player[checkField] !== undefined;
                          }).length
                        )}
                      >
                        {odds ? (
                          <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {odds > 0 ? `+${odds}` : odds}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        ))}
      </div>
    </div>
  );
};

export default NFLDataTable;